import React, { useState, useEffect, useContext } from 'react';
import { Grid, Paper, TextField, InputAdornment, Button, Modal, Typography, useMediaQuery, useTheme, Box, Chip, Popover, IconButton, Snackbar, Menu, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import MicIcon from '@mui/icons-material/Mic';
import { AuthUser } from '../../interfaces/Contexts';
import JobCardss from '../../Components/JobCards';
import Filters from './Filters';
import { getJobs } from '../../Utils/apiService';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import KenColors from '../../Helpers/Themes/kenColors';
import { useStyles } from './styles'; // or '../../styles' if needed
import FilterListIcon from '@mui/icons-material/FilterList';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';
import FilterIcon from '../../Assets/filter.svg';

const JobsViewAll: React.FC = () => {
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;

  const [filter, setFilter] = useState(false);
  const [search, setSearch] = useState('');
  const [allJobsData, setAllJobsData] = useState([]);
  const [bookmarksData, setBookmarksData] = useState([]);
  const [selectedValues, setSelectedValues] = useState<any>({});
  const [jobFilterData, setJobFilterData] = useState({});
  const [originalJobsData, setOriginalJobsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingSelectedValues, setPendingSelectedValues] = useState<any>({});
  const [isMicSnackbarOpen, setIsMicSnackbarOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortOptions = ['A - Z', 'Z - A'];
  const {
    startListening: startJobSearchListening,
    stopListening: stopJobSearchListening,
    isSupported: isJobMicSupported,
    isListening: isJobMicListening,
  } = useSpeechRecognition({
    onResult: (transcript: string) => {
      setSearch(transcript);
    },
    onStart: () => setIsMicSnackbarOpen(true),
    onEnd: () => setIsMicSnackbarOpen(false),
    onError: () => setIsMicSnackbarOpen(false),
  });

  const handleJobMicStart = () => {
    if (!isJobMicSupported) {
      console.log('Speech recognition not supported in this browser.');
      return;
    }
    startJobSearchListening();
  };

  const handleJobMicStop = () => {
    stopJobSearchListening();
  };

  const classes = useStyles();

  useEffect(() => {
    getJobs(userData.applicantId).then((res) => {
      setOriginalJobsData(res.data.allJobs);
      setAllJobsData(res.data.allJobs);
      setJobFilterData(res.data); // Make sure to set filter data for Filters component
    });
  }, []);

  const handleFilterOpen = () => {
    setPendingSelectedValues(selectedValues); // Copy current filters to pending
    setFilter(true);
  };

  const handleFilterCancellation = () => setFilter(false);

  const handleFilterApply = (newValues: any) => {
    setSelectedValues(newValues);
    setFilter(false);
  };

  const handleSortButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortMenuClose = () => setSortAnchorEl(null);

  const handleSortSelect = (option: string) => {
    setSortBy(option);
    handleSortMenuClose();
  };

  const handleChipDelete = (key: string, value: string) => {
    setSelectedValues((prev: any) => {
      const updated = { ...prev };
      if (Array.isArray(updated[key])) {
        updated[key] = updated[key].filter((v: string) => v !== value);
        // Remove key if array is empty
        if (updated[key].length === 0) delete updated[key];
      } else {
        delete updated[key];
      }
      // Special handling for Salary
      if (key === 'Salary') {
        delete updated['Salary_Start_Range__c'];
        delete updated['Salary_End_Range__c'];
      }
      return updated;
    });
  };

  // Filtering logic: search + filter
  const getFilteredJobs = () => {
    const getCreatedTimestamp = (job: any) => new Date(job?.CreatedDate || job?.createdDate || 0).getTime();
    let filtered = [...originalJobsData];

    // Apply filter values
    Object.entries(selectedValues).forEach(([key, value]: any) => {
      if (!value || value.length === 0) return;
      if (key === 'Salary') return; // Salary handled by Salary_Start_Range__c/Salary_End_Range__c
      if (key === 'Organization__r.Name') {
        filtered = filtered.filter((job: any) => value.includes(job.Organization__r?.Name));
      } else if (key === 'Work_Place_Type__c') {
        filtered = filtered.filter((job: any) => job.Work_Place_Type__c && value.some((v: string) => job.Work_Place_Type__c.includes(v)));
      } else if (key === 'Posted_by') {
        if (value.includes('myself')) {
          filtered = filtered.filter((job: any) => job.PostedById === userData.applicantId);
        }
        // 'Anyone' means no filter
      } else if (key === 'Location__c') {
        filtered = filtered.filter((job: any) => value.includes(job.Location__c));
      } else if (key === 'Job_Type__c') {
        filtered = filtered.filter((job: any) => value.includes(job.Job_Type__c));
      } else if (key === 'Skill_Required__c') {
        filtered = filtered.filter((job: any) => job.Skill_Required__c && value.some((v: string) => job.Skill_Required__c.includes(v)));
      } else if (key === 'Job_Title__c') {
        filtered = filtered.filter((job: any) => value.includes(job.Job_Title__c));
      } else if (key === 'Industry__c') {
        filtered = filtered.filter((job: any) => value.includes(job.Industry__c));
      }
      // Add more keys as needed
    });

    // Salary filter
    if (selectedValues.Salary_Start_Range__c && selectedValues.Salary_End_Range__c) {
      filtered = filtered.filter((job: any) => {
        const min = Math.min(...selectedValues.Salary_Start_Range__c);
        const max = Math.max(...selectedValues.Salary_End_Range__c);
        return job.Salary_Start_Range__c >= min && job.Salary_End_Range__c <= max;
      });
    }

    // Posted On filter (using Last_Date_to_Apply__c)
    if (selectedValues['Posted On'] && selectedValues['Posted On'].length > 0) {
      const selectedPostedOn = selectedValues['Posted On'] as string[];
      if (!selectedPostedOn.includes('Anytime')) {
        const now = new Date();
        const cutoffMap: Record<string, number> = {
          'Past 24 hours': 1,
          'Past week': 7,
          'Past Month': 30,
        };
        filtered = filtered.filter((job: any) => {
          const jobDate = job.Last_Date_to_Apply__c ? new Date(`${job.Last_Date_to_Apply__c}T00:00:00`) : null;
          if (!jobDate || Number.isNaN(jobDate.getTime())) return false;
          return selectedPostedOn.some((range: string) => {
            const days = cutoffMap[range];
            if (!days) return false;
            const cutoff = new Date(now);
            cutoff.setDate(now.getDate() - days);
            return jobDate >= cutoff;
          });
        });
      }
    }

    // Search filter
    if (search) {
      filtered = filtered.filter((job: any) => job.Job_Title__c?.toLowerCase().includes(search.toLowerCase()));
    }

    if (sortBy === 'A - Z') {
      return filtered.sort((a: any, b: any) => {
        const titleA = (a?.Job_Title__c || '').toLowerCase();
        const titleB = (b?.Job_Title__c || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
    }
    if (sortBy === 'Z - A') {
      return filtered.sort((a: any, b: any) => {
        const titleA = (a?.Job_Title__c || '').toLowerCase();
        const titleB = (b?.Job_Title__c || '').toLowerCase();
        return titleB.localeCompare(titleA);
      });
    }

    // Default: show most recently created jobs first.
    return filtered.sort((a: any, b: any) => getCreatedTimestamp(b) - getCreatedTimestamp(a));
  };

  const filteredJobs = getFilteredJobs();

  console.log(filteredJobs, 'sjkdwdekjh');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);

  const handleSortButtonClick1 = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl1(event.currentTarget);
  };

  return (
    <Grid style={{ padding: '10px', backgroundColor: 'white', borderRadius: '10px' }} className="scrollbar-cusgrey">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Grid sx={{ p: isMobile ? 1 : 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'nowrap' }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                // '@media (min-width: 1280px) and (max-width: 1300px)': {
                //   fontSize: '11px !important',
                // },
              }}
            >
              All Jobs
            </Typography>
            <Box className="d-flex gap-3" sx={{ alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              {!isMobile && (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Search by Company or Job"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ mt: 0.5 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onMouseDown={handleJobMicStart}
                          onMouseUp={handleJobMicStop}
                          onMouseLeave={handleJobMicStop}
                          onTouchStart={handleJobMicStart}
                          onTouchEnd={handleJobMicStop}
                          onTouchCancel={handleJobMicStop}
                          size="small"
                          sx={{ color: isJobMicListening ? 'error.main' : 'inherit' }}
                        >
                          <MicIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: '260px' },
                    maxWidth: '100%',
                    borderRadius: '12px !important',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      height: 40,
                      paddingRight: 0,
                      fontSize: 12,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '12px',
                    },
                  }}
                />
              )}
              <Button size="large" variant="contained" color="info" endIcon={<img src={FilterIcon} alt="filter" style={{ width: 24, height: 24 }} />} onClick={handleSortButtonClick}>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{sortBy ? sortBy : 'Sort by'}</Typography>
              </Button>
              <Menu
                anchorEl={sortAnchorEl}
                open={Boolean(sortAnchorEl)}
                onClose={handleSortMenuClose}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    // minWidth: 180,
                    boxShadow: 3,
                    mt: 1,
                    backgroundColor: '#FFFFFF',
                  },
                }}
              >
                <MenuItem onClick={() => handleSortSelect('')} selected={sortBy === ''} sx={{ fontWeight: sortBy === '' ? 600 : 400 }}>
                  Clear
                </MenuItem>
                {sortOptions.map((option) => (
                  <MenuItem key={option} onClick={() => handleSortSelect(option)} selected={sortBy === option} sx={{ fontWeight: sortBy === option ? 600 : 400 }}>
                    {option}
                  </MenuItem>
                ))}
              </Menu>
              <Button
                size="large"
                variant="contained"
                color="info"
                endIcon={<FilterListIcon style={{ fontSize: '24px' }} />}
                // onClick={handleFilterOpen}
                onClick={(event) => {
                  // toggleFilter();
                  handleFilterOpen();
                  handleSortButtonClick1(event);

                  // handleSortButtonClick1(event);
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Filters</Typography>
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      {/* Filter Chips - moved here, above job cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {Object.entries(selectedValues).map(([key, values]: [string, any]) => {
          if (!values || key.endsWith('_Range__c')) return null; // Skip salary range keys
          if (Array.isArray(values)) {
            return values.map((val: string) => (
              <Chip
                key={key + val}
                label={`${key === 'Organization__r.Name' ? 'Company' : key.replace(/__/g, '').replace(/_/g, ' ')}: ${val}`}
                onDelete={() => handleChipDelete(key, val)}
                sx={{ fontSize: '12px', m: 0.5 }}
              />
            ));
          }
          return <Chip key={key + values} label={`${key}: ${values}`} onDelete={() => handleChipDelete(key, values)} sx={{ fontSize: '12px', m: 0.5 }} />;
        })}
      </Box>
      {/* End Filter Chips */}
      <Grid container columnSpacing={isMobile ? 2 : 2} rowSpacing={isMobile ? 2 : 4} sx={{ width: '100%' }}>
        {filteredJobs.map((val: any, index: number) => (
          <Grid item xs={12} sm={12} md={12} xl={4} lg={4} key={val.Id}>
            <JobCardss
              value={val}
              userData={userData}
              isBookmarked={bookmarksData.some((job: any) => job.Id === val.Id)}
              onBookmarkClick={() => console.log('Bookmark clicked')}
              setIsBookmarked={() => console.log('Set bookmark')}
            />
          </Grid>
        ))}
      </Grid>
      {/* Filter Modal */}

      <Popover
        anchorEl={anchorEl1}
        open={filter}
        onClose={handleFilterCancellation}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 300,
            boxShadow: 3,
            mt: 1,
            backgroundColor: '#FFFFFF',
            p: 2,
          },
        }}
      >
        <Filters
          handleClose={handleFilterCancellation}
          jobFilterData={jobFilterData}
          selectedValues={pendingSelectedValues}
          setSelectedValues={setPendingSelectedValues}
          setLoading={setLoading}
          handleDropDownChange={handleFilterApply}
          // modalLeft={{ xs: '50%', sm: '50%', md: '68%', lg: '68%', xl: '68%' }}
          fromJobsViewAll={true}
        />
      </Popover>
      <Snackbar
        open={isMicSnackbarOpen}
        message={isJobMicSupported ? 'Listening for job search…' : 'Speech recognition not supported.'}
        onClose={() => setIsMicSnackbarOpen(false)}
        autoHideDuration={isJobMicSupported ? 2000 : 4000}
      />
    </Grid>
  );
};

export default JobsViewAll;
